/**
 * Component Lifecycle Manager
 * Provides standardized lifecycle management for components
 * @author Zubduq Team
 * @version 1.0.0
 */

import memoryLeakPrevention from './memoryLeakPrevention.js';

class ComponentLifecycleManager {
  constructor() {
    this.components = new Map();
    this.cleanupCallbacks = new Map();
  }

  /**
   * Register a component with lifecycle management
   */
  registerComponent(name, instance, options = {}) {
    // Unregister existing component if it exists
    if (this.components.has(name)) {
      this.unregisterComponent(name);
    }

    const config = {
      instance,
      hasDestroy: typeof instance.destroy === 'function',
      hasCleanup: typeof instance.cleanup === 'function',
      hasRemoveListeners: typeof instance.removeListeners === 'function',
      autoCleanup: options.autoCleanup !== false,
      cleanupCallbacks: [],
      ...options
    };

    this.components.set(name, config);
    
    // Register with memory leak prevention
    memoryLeakPrevention.registerComponent(name, instance);
    
    return config;
  }

  /**
   * Unregister and cleanup a component
   */
  unregisterComponent(name) {
    const config = this.components.get(name);
    if (!config) {
      console.warn(`⚠️ ComponentLifecycleManager: Component '${name}' not found`);
      return false;
    }

    this.cleanupComponent(name, config);
    this.components.delete(name);
    
    // Unregister from memory leak prevention (only if it exists)
    memoryLeakPrevention.unregisterComponent(name);
    
    return true;
  }

  /**
   * Unregister a component without calling cleanup (for manual cleanup scenarios)
   */
  unregisterComponentOnly(name) {
    const config = this.components.get(name);
    if (!config) {
      console.warn(`⚠️ ComponentLifecycleManager: Component '${name}' not found`);
      return false;
    }

    this.components.delete(name);
    
    // Unregister from memory leak prevention (only if it exists)
    memoryLeakPrevention.unregisterComponent(name);
    
    return true;
  }

  /**
   * Add interval tracking for a component
   */
  addInterval(componentName, callback, delay, description = '') {
    const intervalId = setInterval(callback, delay);
    memoryLeakPrevention.addInterval(componentName, intervalId, description);
    
    console.log(`⏰ Added interval for component '${componentName}': ${intervalId} (${delay}ms)${description ? ' - ' + description : ''}`);
    return intervalId;
  }

  /**
   * Add timeout tracking for a component
   */
  addTimeout(componentName, callback, delay, description = '') {
    const timeoutId = setTimeout(callback, delay);
    memoryLeakPrevention.addTimeout(componentName, timeoutId, description);
    
    console.log(`⏱️ Added timeout for component '${componentName}': ${timeoutId} (${delay}ms)${description ? ' - ' + description : ''}`);
    return timeoutId;
  }

  /**
   * Add event listener with automatic cleanup tracking
   */
  addEventListener(componentName, element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    memoryLeakPrevention.addEventListener(componentName, element, event, handler, options);
    
    console.log(`👂 Added event listener for component '${componentName}': ${event} on ${element.constructor.name}`);
    
    // Return cleanup function
    return () => {
      element.removeEventListener(event, handler, options);
      console.log(`👂 Removed event listener for component '${componentName}': ${event} on ${element.constructor.name}`);
    };
  }

  /**
   * Add observer with automatic cleanup tracking
   */
  addObserver(componentName, observer, description = '') {
    memoryLeakPrevention.addObserver(componentName, observer, description);
    
    console.log(`👁️ Added observer for component '${componentName}'${description ? ': ' + description : ''}`);
    
    // Return cleanup function
    return () => {
      if (observer && typeof observer.disconnect === 'function') {
        observer.disconnect();
        console.log(`👁️ Disconnected observer for component '${componentName}'${description ? ': ' + description : ''}`);
      }
    };
  }

  /**
   * Add DOM reference tracking
   */
  addDomReference(componentName, element, description = '') {
    memoryLeakPrevention.addDomReference(componentName, element, description);
    
    console.log(`🏠 Added DOM reference for component '${componentName}'${description ? ': ' + description : ''}`);
  }

  /**
   * Add cleanup callback for a component
   */
  addCleanupCallback(componentName, callback, description = '') {
    const config = this.components.get(componentName);
    if (config) {
      config.cleanupCallbacks.push({ callback, description });
      console.log(`🔗 Added cleanup callback for component '${componentName}'${description ? ': ' + description : ''}`);
    }
  }

  /**
   * Clean up only lifecycle manager resources (callbacks, listeners, etc.) without calling component methods
   */
  cleanupComponentResources(name, config = null) {
    if (!config) {
      config = this.components.get(name);
    }
    
    if (!config) {
      console.warn(`⚠️ ComponentLifecycleManager: No config found for component '${name}'`);
      return;
    }
    
    // Run custom cleanup callbacks only (event listeners, timers, DOM references, etc.)
    config.cleanupCallbacks.forEach(({ callback, description }) => {
      try {
        callback();
        console.log(`🔗 Executed cleanup callback for '${name}'${description ? ': ' + description : ''}`);
      } catch (error) {
        console.error(`❌ Error in cleanup callback for '${name}':`, error);
      }
    });

    console.log(`✅ ComponentLifecycleManager: Resources cleaned up for '${name}'`);
  }

  /**
   * Clean up a specific component
   */
  cleanupComponent(name, config = null) {
    if (!config) {
      config = this.components.get(name);
    }
    
    if (!config) {
      console.warn(`⚠️ ComponentLifecycleManager: No config found for component '${name}'`);
      return;
    }

    const { instance } = config;
    
    // Run custom cleanup callbacks first
    config.cleanupCallbacks.forEach(({ callback, description }) => {
      try {
        callback();
        console.log(`🔗 Executed cleanup callback for '${name}'${description ? ': ' + description : ''}`);
      } catch (error) {
        console.error(`❌ Error in cleanup callback for '${name}':`, error);
      }
    });

    // Call component cleanup methods in order of preference
    if (config.hasDestroy) {
      try {
        instance.destroy();
        console.log(`🧹 Called destroy() for component '${name}'`);
      } catch (error) {
        console.error(`❌ Error calling destroy() for component '${name}':`, error);
      }
    } else if (config.hasCleanup) {
      try {
        instance.cleanup();
        console.log(`🧹 Called cleanup() for component '${name}'`);
      } catch (error) {
        console.error(`❌ Error calling cleanup() for component '${name}':`, error);
      }
    } else if (config.hasRemoveListeners) {
      try {
        instance.removeListeners();
        console.log(`🧹 Called removeListeners() for component '${name}'`);
      } catch (error) {
        console.error(`❌ Error calling removeListeners() for component '${name}':`, error);
      }
    }

    console.log(`✅ ComponentLifecycleManager: Component '${name}' cleaned up`);
  }

  /**
   * Clean up all registered components
   */
  cleanupAll() {
    console.log('🧹 ComponentLifecycleManager: Starting cleanup of all components...');
    
    const componentNames = Array.from(this.components.keys());
    let cleanedCount = 0;
    let errorCount = 0;

    componentNames.forEach(name => {
      try {
        this.unregisterComponent(name);
        cleanedCount++;
      } catch (error) {
        console.error(`❌ Error cleaning up component '${name}':`, error);
        errorCount++;
      }
    });

    console.log(`✅ ComponentLifecycleManager: Cleanup completed. ${cleanedCount} components cleaned, ${errorCount} errors`);
  }

  /**
   * Get status of all registered components
   */
  getComponentStatus() {
    const status = [];
    
    this.components.forEach((config, name) => {
      status.push({
        name,
        hasDestroy: config.hasDestroy,
        hasCleanup: config.hasCleanup,
        hasRemoveListeners: config.hasRemoveListeners,
        autoCleanup: config.autoCleanup,
        cleanupCallbacks: config.cleanupCallbacks.length
      });
    });

    return {
      totalComponents: this.components.size,
      components: status
    };
  }

  /**
   * Create a mixin for components to easily integrate lifecycle management
   */
  createMixin(componentName) {
    const manager = this;
    
    return {
      // Lifecycle management methods
      addInterval(callback, delay, description) {
        return manager.addInterval(componentName, callback, delay, description);
      },
      
      addTimeout(callback, delay, description) {
        return manager.addTimeout(componentName, callback, delay, description);
      },
      
      addEventListener(element, event, handler, options) {
        return manager.addEventListener(componentName, element, event, handler, options);
      },
      
      addObserver(observer, description) {
        return manager.addObserver(componentName, observer, description);
      },
      
      addDomReference(element, description) {
        return manager.addDomReference(componentName, element, description);
      },
      
      addCleanupCallback(callback, description) {
        return manager.addCleanupCallback(componentName, callback, description);
      },
      
      // Cleanup method to be called in component destroy/cleanup
      // Clean up lifecycle manager resources without calling component cleanup again
      lifecycleCleanup() {
        const config = manager.components.get(componentName);
        if (config) {
          // Clean up the lifecycle manager's internal resources only
          manager.cleanupComponentResources(componentName, config);
          // Remove from registry without calling component cleanup
          manager.unregisterComponentOnly(componentName);
        }
      }
    };
  }
}

// Create singleton instance
const componentLifecycleManager = new ComponentLifecycleManager();

// Setup automatic cleanup on page unload
window.addEventListener('beforeunload', () => {
  componentLifecycleManager.cleanupAll();
});

window.addEventListener('pagehide', () => {
  componentLifecycleManager.cleanupAll();
});

// Export for use in other modules
export default componentLifecycleManager;

// Global access for debugging
window.componentLifecycleManager = componentLifecycleManager;

console.log('🔧 Component Lifecycle Manager loaded');
